import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { toast } from "sonner";
import { crmPatch, crmReply, errMsg } from "@/services/crmApi";
import { text, fmtTs } from "@/lib/format";

export const TICKET_STATUSES = ["open", "in_progress", "awaiting_customer", "resolved", "closed"];
const PRIORITIES = ["urgent", "high", "normal", "low"];

// Works for support_conversations (Brugo Pro) and support_tickets (all stakeholders).
export function TicketDrawer({ coll, t }) {
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState(t.status || "open");
  const [busy, setBusy] = useState(false);
  const who = coll === "support_conversations" ? `Professional · ${t.business_name || t.professional_id}` : `${t.stakeholder_type} · ${t.stakeholder_name || t.stakeholder_phone || t.stakeholder_uid || ""}`;
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await crmReply(coll, t.id, msg.trim(), status !== t.status ? status : undefined); setMsg(""); toast.success("Reply sent"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const patch = async (fields) => {
    try { await crmPatch(coll, t.id, fields); toast.success("Updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <SheetHeader className="text-left">
        <SheetTitle className="font-heading">{text(t.subject, "Support ticket")}</SheetTitle>
        <SheetDescription className="capitalize">{who}{t.request_id ? ` · request ${t.request_id}` : ""}{t.project_id ? ` · project ${t.project_id}` : ""}</SheetDescription>
      </SheetHeader>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Status</Label>
          <Select value={t.status || "open"} onValueChange={(v) => { setStatus(v); patch({ status: v }); }}>
            <SelectTrigger data-testid="ticket-status"><SelectValue /></SelectTrigger>
            <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Priority</Label>
          <Select value={t.priority || "normal"} onValueChange={(v) => patch({ priority: v })}>
            <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="ticket-messages">
        {(t.messages || []).map((m, i) => {
          const ours = m.from === "brugo_support";
          return (
            <div key={i} className={`rounded-lg px-3 py-2 text-sm max-w-[85%] ${ours ? "ml-auto bg-primary text-primary-foreground" : "bg-secondary"}`}>
              <p className="whitespace-pre-wrap break-words">{text(m.text)}</p>
              <p className={`text-[10px] mt-1 ${ours ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{ours ? m.by_name || "Brugo support" : m.from} · {fmtTs(m.at)}</p>
            </div>
          );
        })}
        {!(t.messages || []).length && <p className="text-sm text-muted-foreground">No messages yet.</p>}
      </div>
      <div className="mt-4 space-y-2">
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a reply…" rows={3} data-testid="ticket-reply-input" />
        <div className="flex items-center justify-between">
          <StatusBadge status={t.status || "open"} />
          <Button onClick={send} disabled={busy || !msg.trim()} data-testid="ticket-reply-send">{busy ? "Sending…" : "Send reply"}</Button>
        </div>
      </div>
    </>
  );
}
